// AuthStore: the auth state the router guards read outside React (`beforeLoad`), and the AuthProvider exposes to
// components (Frontend §5.1, §5.3). One per app.
import type { AuthClient, AuthUser } from './client'

export type AuthStatus = 'loading' | 'signedOut' | 'signedIn'

export interface AuthSnapshot {
  status: AuthStatus
  user: AuthUser | null
}

async function sha256(text: string): Promise<string> {
  const bytes = new TextEncoder().encode(text)
  const digest = await crypto.subtle.digest('SHA-256', bytes)
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('')
}

export class AuthStore {
  private snapshot: AuthSnapshot = { status: 'loading', user: null }
  private readonly listeners = new Set<() => void>()
  private readonly readyPromise: Promise<void>
  /**
   * A one-way hash of the password typed at the last sign-in, kept in memory only, so A-04 can refuse the
   * temporary password as the new one (UserFlows A-04, T-A04-03). Never stored or sent anywhere.
   */
  private lastSignInHash: string | undefined
  private signingInFlag = false

  /** True while A-01 is signing in: a `401` from `/v1/me` then shows the generic A-01 message, not A-06. */
  get signingIn(): boolean {
    return this.signingInFlag
  }

  setSigningIn(value: boolean): void {
    this.signingInFlag = value
  }

  constructor(readonly client: AuthClient) {
    let resolve: () => void = () => undefined
    this.readyPromise = new Promise<void>((r) => {
      resolve = r
    })
    client.onChange((user) => {
      this.snapshot = { status: user ? 'signedIn' : 'signedOut', user }
      resolve()
      for (const l of this.listeners) l()
    })
  }

  /** Resolves once Firebase has restored (or not) the session. */
  ready(): Promise<void> {
    return this.readyPromise
  }

  get status(): AuthStatus {
    return this.snapshot.status
  }

  get user(): AuthUser | null {
    return this.snapshot.user
  }

  /** For `useSyncExternalStore`. */
  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  getSnapshot = (): AuthSnapshot => this.snapshot

  async signIn(email: string, password: string): Promise<void> {
    await this.client.signIn(email, password)
    this.lastSignInHash = await sha256(password)
  }

  async signOut(): Promise<void> {
    this.lastSignInHash = undefined
    await this.client.signOut()
  }

  private quietFlag = false

  /** True during `signOutQuietly()`: the session's cross-tab handler leaves the navigation to the caller. */
  get signingOutQuietly(): boolean {
    return this.quietFlag
  }

  /** A sign-out whose caller navigates itself (a guard redirecting to A-01 with a reason, task 13). */
  async signOutQuietly(): Promise<void> {
    this.quietFlag = true
    try {
      await this.signOut()
    } finally {
      this.quietFlag = false
    }
  }

  /** A-04: is this the password the user signed in with (their temporary one)? */
  async isLastSignInPassword(password: string): Promise<boolean> {
    return this.lastSignInHash !== undefined && this.lastSignInHash === (await sha256(password))
  }

  async changePassword(newPassword: string): Promise<void> {
    await this.client.changePassword(newPassword)
    this.lastSignInHash = undefined
  }

  /** A-02 (Frontend §5.1). */
  sendPasswordReset(email: string, continueUrl: string): Promise<void> {
    return this.client.sendPasswordReset(email, continueUrl)
  }

  /** A-03: the link's account email. */
  verifyPasswordResetCode(oobCode: string): Promise<string> {
    return this.client.verifyPasswordResetCode(oobCode)
  }

  /** A-03. */
  confirmPasswordReset(oobCode: string, newPassword: string): Promise<void> {
    return this.client.confirmPasswordReset(oobCode, newPassword)
  }

  getIdToken = (forceRefresh?: boolean): Promise<string | null> => this.client.getIdToken(forceRefresh)
}
